import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, User, UserPlus } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useState } from 'react';
import { API_URL } from '../config/api';

const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api`;

const CalendarSelector = ({ calendars, selectedCalendar, onSelectCalendar, onCalendarsChanged }) => {
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);

  const handleInvite = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Veuillez entrer un email');
      return;
    }

    setInviting(true);
    try {
      await axios.post(
        `${API}/calendars/${selectedCalendar.id}/invite`,
        { email: inviteEmail },
        { withCredentials: true }
      );
      toast.success('Invitation envoyée !');
      setShowInviteDialog(false);
      setInviteEmail('');
      onCalendarsChanged();
    } catch (error) {
      console.error('Invite error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'invitation');
    } finally {
      setInviting(false);
    }
  };

  const canInvite = selectedCalendar?.type === 'joint' && !selectedCalendar?.shared_with_user_id;

  return (
    <>
      {/* Premium Card - Clean Design */}
      <div className="premium-card mb-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Calendrier</p>
            <Select
              value={selectedCalendar?.id}
              onValueChange={(id) => {
                const calendar = calendars.find(c => c.id === id);
                onSelectCalendar(calendar);
              }}
            >
              <SelectTrigger 
                className="w-full h-11 rounded-xl border-border/50 bg-muted/30 focus:ring-2 focus:ring-primary/20" 
                data-testid="calendar-selector"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                {calendars.map((calendar) => (
                  <SelectItem key={calendar.id} value={calendar.id} className="rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${
                        calendar.type === 'joint' ? 'bg-primary/10' : 'bg-muted'
                      }`}>
                        {calendar.type === 'joint' ? (
                          <Users className="h-3.5 w-3.5 text-primary" strokeWidth={1.5} />
                        ) : (
                          <User className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
                        )}
                      </div>
                      <span className="font-medium text-foreground">{calendar.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {canInvite && (
            <Button
              onClick={() => setShowInviteDialog(true)}
              variant="ghost"
              size="icon"
              className="h-10 w-10 rounded-xl flex-shrink-0"
              data-testid="invite-button"
            >
              <UserPlus className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
            </Button>
          )}

          {selectedCalendar?.shared_with_user_id && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-secondary text-secondary-foreground text-xs font-medium">
              <Users className="h-3.5 w-3.5" strokeWidth={1.5} />
              <span>Partagé</span>
            </div>
          )}
        </div>
      </div>

      {/* Invite Dialog - Premium Style */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="rounded-2xl max-w-sm" data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold tracking-tight">Inviter quelqu'un</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Partagez ce calendrier avec un autre utilisateur.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="invite-email" className="text-sm font-medium">Email</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="email@exemple.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="h-11 rounded-xl bg-muted/30 border-border/50"
                data-testid="invite-email-input"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                variant="ghost"
                onClick={() => setShowInviteDialog(false)}
                className="flex-1 h-11 rounded-xl"
              >
                Annuler
              </Button>
              <Button
                onClick={handleInvite}
                disabled={inviting}
                className="flex-1 h-11 rounded-xl bg-primary hover:bg-primary/90"
                data-testid="send-invite-button"
              >
                {inviting ? 'Envoi...' : 'Inviter'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CalendarSelector;